// app/frontend/components/ui/Navigation/Navbar.tsx
import { useState, useMemo } from "react";
import { Group, Burger, Box, UnstyledButton, ActionIcon, Kbd } from "@mantine/core";
import { spotlight } from "@mantine/spotlight";
import type { SpotlightActionData } from "@mantine/spotlight";
import { IconVideo, IconBook, IconSearch, IconLayoutDashboard, IconUsers, IconNews } from "@tabler/icons-react";
import { Link, usePage, router } from "@inertiajs/react";
import { L2KH4, L2KText } from "../Typography";
import UserSection from "./UserSection";
import SpotlightSearch from "./SpotlightSearch";

interface User {
  id: number;
  email: string;
  name: string;
  admin: boolean;
}

interface NavbarProps {
  drawerOpened: boolean;
  toggleDrawer: () => void;
}

interface NavLinkProps {
  href: string;
  icon: React.ReactNode;
  label: string;
  active?: boolean;
  disabled?: boolean;
  disabledTitle?: string;
}

function NavLink({ href, icon, label, active, disabled, disabledTitle }: NavLinkProps) {
  const inner = (
    <UnstyledButton
      component={disabled ? "span" : undefined}
      title={disabled ? disabledTitle : undefined}
      aria-disabled={disabled || undefined}
      style={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        padding: "8px 12px",
        borderRadius: "8px",
        background: active ? "rgba(255, 152, 0, 0.15)" : "transparent",
        transition: "all 0.2s ease",
        opacity: disabled ? 0.5 : 1,
        cursor: disabled ? "not-allowed" : undefined,
      }}
      onMouseEnter={(e) => {
        if (!active && !disabled) {
          e.currentTarget.style.background = "rgba(255, 152, 0, 0.1)";
        }
      }}
      onMouseLeave={(e) => {
        if (!active && !disabled) {
          e.currentTarget.style.background = "transparent";
        }
      }}
    >
      <Box
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          lineHeight: 0,
          color: active ? "#FF9800" : "#888",
        }}
      >
        {icon}
      </Box>
      <L2KText
        style={{
          color: active ? "#FF9800" : "#E0E0E0",
          fontWeight: active ? 600 : 400,
          fontSize: 12,
          letterSpacing: "0.02em",
          lineHeight: 1,
        }}
      >
        {label}
      </L2KText>
      {disabled && (
        <L2KText
          style={{
            color: "rgba(255, 152, 0, 0.7)",
            fontSize: 10,
            fontWeight: 600,
            letterSpacing: "0.04em",
            textTransform: "uppercase",
            padding: "2px 6px",
            borderRadius: 4,
            background: "rgba(255, 152, 0, 0.12)",
            lineHeight: 1,
          }}
        >
          Soon
        </L2KText>
      )}
    </UnstyledButton>
  );

  if (disabled) return inner;

  return (
    <Link href={href} style={{ textDecoration: "none" }}>
      {inner}
    </Link>
  );
}

export function Navbar({ drawerOpened, toggleDrawer }: NavbarProps) {
  const { current_user, url } = usePage<{ current_user: User | null; url: string }>().props;
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);

  const isActive = (path: string) => {
    if (typeof url === "string") {
      return url.startsWith(path);
    }
    return false;
  };

  // Generate spotlight actions based on search query
  const spotlightActions: SpotlightActionData[] = useMemo(() => {
    const baseActions: SpotlightActionData[] = [
      {
        id: "dashboard",
        label: "Dashboard",
        description: "Coming soon",
        leftSection: <IconLayoutDashboard size={20} />,
        disabled: true,
        onClick: () => {},
      },
      {
        id: "videos",
        label: "Videos",
        description: "Browse all videos",
        leftSection: <IconVideo size={20} />,
        onClick: () => router.visit("/videos"),
      },
      {
        id: "posts",
        label: "Posts",
        description: "Long-form writing and podcast episodes",
        leftSection: <IconNews size={20} />,
        onClick: () => router.visit("/posts"),
      },
      {
        id: "dictionary",
        label: "Dictionary",
        description: "Dance terminology dictionary",
        leftSection: <IconBook size={20} />,
        onClick: () => router.visit("/dictionary"),
      },
      {
        id: "community",
        label: "Community",
        description: "Coming soon",
        leftSection: <IconUsers size={20} />,
        disabled: true,
        onClick: () => {},
      },
    ];

    // If there's a search query, add a search action
    if (searchQuery.trim()) {
      return [
        {
          id: "search-videos",
          label: `Search videos for "${searchQuery}"`,
          description: "Find videos matching your search",
          leftSection: <IconSearch size={20} />,
          onClick: () => router.visit(`/videos?search=${encodeURIComponent(searchQuery)}`),
        },
        ...baseActions,
      ];
    }

    return baseActions;
  }, [searchQuery]);

  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
  };

  const handleSearchClick = () => {
    spotlight.open();
  };

  return (
    <>
      {/* Spotlight Search */}
      <SpotlightSearch
        actions={spotlightActions}
        onSearchChange={handleSearchChange}
        isSearching={isSearching}
      />

      <Group h="100%" px="md" justify="space-between" align="center" style={{ width: "100%" }}>
        {/* Left Section: Burger + Logo */}
        <Group gap="md">
          <Burger
            opened={drawerOpened}
            onClick={toggleDrawer}
            size="sm"
            color="rgba(255, 152, 0, 0.8)"
            hiddenFrom="md"
          />

          <Link href={current_user ? "/dashboard" : "/"} style={{ textDecoration: "none" }}>
            <L2KH4 mb={0}>
              <Box component="span" hiddenFrom="md">
                <span style={{ color: "#FF9800" }}>L2K</span>
                <span style={{ color: "#F0F0F0" }}> 2.0</span>
              </Box>
              <Box component="span" visibleFrom="md">
                <span style={{ color: "#FF9800" }}>Learn</span>
                <span style={{ color: "#F0F0F0" }}>tokiz</span>
              </Box>
            </L2KH4>
          </Link>

          {/* Search Button */}
          <ActionIcon
            size="lg"
            variant="default"
            onClick={handleSearchClick}
            title="Search (Cmd/Ctrl+K)"
            aria-label="Open search"
            ml="xs"
            style={{
              background: "rgba(255, 152, 0, 0.08)",
              border: "1px solid rgba(255, 152, 0, 0.15)",
              borderRadius: "8px",
              transition: "all 0.2s ease",
            }}
          >
            <IconSearch strokeWidth={2} color="#888" style={{ width: "18px", height: "18px" }} />
          </ActionIcon>

          {/* Keyboard shortcut hint (desktop only) */}
          <Box visibleFrom="md" style={{ display: "flex", alignItems: "center", gap: "4px" }}>
            <Kbd size="xs" style={{ background: "rgba(255, 255, 255, 0.05)", border: "1px solid rgba(255, 255, 255, 0.1)", color: "#888" }}>
              Cmd
            </Kbd>
            <span style={{ color: "#666", fontSize: "12px" }}>+</span>
            <Kbd size="xs" style={{ background: "rgba(255, 255, 255, 0.05)", border: "1px solid rgba(255, 255, 255, 0.1)", color: "#888" }}>
              K
            </Kbd>
          </Box>

          {/* Desktop Navigation Links */}
          <Group gap="xs" visibleFrom="md" ml="lg">
            <NavLink
              href="/videos"
              icon={<IconVideo size={18} />}
              label="Videos"
              active={isActive("/videos")}
            />
            <NavLink
              href="/posts"
              icon={<IconNews size={18} />}
              label="Posts"
              active={isActive("/posts")}
            />
            <NavLink
              href="/dictionary"
              icon={<IconBook size={18} />}
              label="Dictionary"
              active={isActive("/dictionary")}
            />
            <NavLink
              href="/community"
              icon={<IconUsers size={18} />}
              label="Community"
              disabled
              disabledTitle="Community launches with 2.0"
            />
          </Group>
        </Group>

      {/* Right Section: User menu or desktop auth buttons */}
      {current_user ? (
        <Group gap="sm">
          <UserSection user={current_user} />
        </Group>
      ) : (
        <Group gap="sm" visibleFrom="md">
          <Link
            href="/login"
            style={{
              display: "inline-flex",
              alignItems: "center",
              padding: "8px 16px",
              background: "transparent",
              border: "1px solid rgba(255, 255, 255, 0.2)",
              borderRadius: "8px",
              color: "#E0E0E0",
              textDecoration: "none",
              fontFamily: "'Public Sans', sans-serif",
              fontWeight: 500,
              fontSize: "14px",
              transition: "all 0.2s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = "rgba(255, 152, 0, 0.5)";
              e.currentTarget.style.color = "#FF9800";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.2)";
              e.currentTarget.style.color = "#E0E0E0";
            }}
          >
            Log in
          </Link>
          <span
            title="Sign up opens at 2.0 launch"
            aria-disabled="true"
            style={{
              display: "inline-flex",
              alignItems: "center",
              padding: "8px 20px",
              background: "rgba(255, 152, 0, 0.15)",
              border: "1px solid rgba(255, 152, 0, 0.25)",
              borderRadius: "8px",
              color: "rgba(255, 152, 0, 0.55)",
              fontFamily: "'Public Sans', sans-serif",
              fontWeight: 600,
              fontSize: "14px",
              cursor: "not-allowed",
              userSelect: "none",
            }}
          >
            Sign up · Coming soon
          </span>
        </Group>
      )}
      </Group>
    </>
  );
}

export default Navbar;
